import React from 'react';
import { Stack, Button } from 'react-bootstrap';
import { createEvent } from 'ics';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

const AddToCalendarLinks = ({ meeting, salon, formatParticipantName }) => {
  // 1. Préparer les données de l'événement
  const eventTitle = `Rendez-vous Tred'Unisson : ${
    meeting.associatedEntities?.join(', ') || 'Participant'
  }`;
  const eventDescription = `Participants : ${
    meeting.associatedParticipants
      ?.map(p => formatParticipantName(p, true))
      .join(', ') || 'Non spécifié'
  }`;
  const location = salon.name;

  const datePart = salon.date.split('T')[0];
  const startDateTime = new Date(`${datePart}T${meeting.start_time}`);
  const endDateTime = new Date(`${datePart}T${meeting.end_time}`);

  if (isNaN(startDateTime) || isNaN(endDateTime)) {
    return null; // Ne rien afficher si les dates sont invalides
  }

  // 2. Formater les dates pour chaque service
  const formatForGoogle = date =>
    date.toISOString().replace(/-|:|\.\d{3}/g, '');
  const formatForYahoo = date => formatForGoogle(date).slice(0, -1); // Yahoo n'aime pas le 'Z' à la fin

  // 3. Construire les URLs
  const googleUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
    eventTitle
  )}&dates=${formatForGoogle(startDateTime)}/${formatForGoogle(
    endDateTime
  )}&details=${encodeURIComponent(
    eventDescription
  )}&location=${encodeURIComponent(location)}`;

  const yahooUrl = `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(
    eventTitle
  )}&st=${formatForYahoo(startDateTime)}&et=${formatForYahoo(
    endDateTime
  )}&desc=${encodeURIComponent(eventDescription)}&in_loc=${encodeURIComponent(
    location
  )}`;

  const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(
    eventTitle
  )}&startdt=${startDateTime.toISOString()}&enddt=${endDateTime.toISOString()}&body=${encodeURIComponent(
    eventDescription
  )}&location=${encodeURIComponent(location)}`;

  // 4. Gérer le téléchargement du fichier .ics
  const handleDownloadIcs = () => {
    const event = {
      title: eventTitle,
      description: eventDescription,
      location: location,
      start: [
        startDateTime.getFullYear(),
        startDateTime.getMonth() + 1,
        startDateTime.getDate(),
        startDateTime.getHours(),
        startDateTime.getMinutes()
      ],
      end: [
        endDateTime.getFullYear(),
        endDateTime.getMonth() + 1,
        endDateTime.getDate(),
        endDateTime.getHours(),
        endDateTime.getMinutes()
      ]
    };

    createEvent(event, (error, value) => {
      if (error) {
        toast.error("Erreur lors de la création de l'événement.");
        return;
      }
      const blob = new Blob([value], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'rendez-vous.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  return (
    <div className="mt-2">
      <small className="me-2">Ajouter à mon calendrier :</small>
      <Stack direction="horizontal" gap={1} className="d-inline-flex">
        <Button
          as="a"
          href={googleUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="link"
          className="p-0"
        >
          Google
        </Button>
        <span>|</span>
        <Button
          as="a"
          href={yahooUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="link"
          className="p-0"
        >
          Yahoo
        </Button>
        <span>|</span>
        <Button
          as="a"
          href={outlookUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="link"
          className="p-0"
        >
          Outlook
        </Button>
        <span>|</span>
        <Button variant="link" className="p-0" onClick={handleDownloadIcs}>
          Enregistrer
        </Button>
      </Stack>
    </div>
  );
};

AddToCalendarLinks.propTypes = {
  meeting: PropTypes.object.isRequired,
  salon: PropTypes.object.isRequired,
  formatParticipantName: PropTypes.func.isRequired
};

export default AddToCalendarLinks;
